import Dexie from 'dexie';

export const db = new Dexie('POSDatabase');

db.version(1).stores({
  users: '++id, username',
  stations: '++id, name, active',
  products: '++id, name, price, category, active',
  orders: '++id, stationId, userId, createdAt, total, status',
  orderItems: '++id, orderId, productId, quantity, price'
});

// Version 2: Add uuid and username to orders for offline sync and audit
db.version(2).stores({
  users: '++id, username',
  stations: '++id, name, active',
  products: '++id, name, price, category, active',
  orders: '++id, uuid, stationId, userId, username, createdAt, total, status, syncedAt',
  orderItems: '++id, orderId, productId, quantity, price'
});

// Version 3: Add nodes table for POS nodes within spaces
db.version(3).stores({
  users: '++id, username',
  stations: '++id, name, active',
  nodes: '++id, spaceId, name, active',
  products: '++id, name, price, category, active',
  orders: '++id, uuid, stationId, nodeId, userId, username, createdAt, total, status, syncedAt',
  orderItems: '++id, orderId, productId, quantity, price'
});

// Version 4: Add uploadStatus and remoteId to support per-order/item sync tracking
db.version(4).stores({
  users: '++id, username',
  stations: '++id, name, active',
  nodes: '++id, spaceId, name, active',
  products: '++id, name, price, category, active',
  orders: '++id, uuid, stationId, nodeId, userId, username, createdAt, total, status, uploadStatus, syncedAt, remoteId',
  orderItems: '++id, orderId, productId, quantity, price, uploadStatus'
});

// Version 5: Add nodeId index to products for per-node offline filtering
db.version(5).stores({
  users: '++id, username',
  stations: '++id, name, active',
  nodes: '++id, spaceId, name, active',
  products: '++id, name, price, category, active, nodeId',
  orders: '++id, uuid, stationId, nodeId, userId, username, createdAt, total, status, uploadStatus, syncedAt, remoteId',
  orderItems: '++id, orderId, productId, quantity, price, uploadStatus'
});

// Version 6: Add productsByStations to store full spaces-tree assignments
db.version(6).stores({
  users: '++id, username',
  stations: '++id, name, active, spaceId',
  nodes: '++id, spaceId, name, active',
  products: '++id, name, price, category, active, nodeId',
  orders: '++id, uuid, stationId, nodeId, userId, username, createdAt, total, status, uploadStatus, syncedAt, remoteId',
  orderItems: '++id, orderId, productId, quantity, price, uploadStatus',
  productsByStations: '++id, spaceId, stationId, productId, name, active'
});

// Version 7: Add spaces table to persist top-level spaces from spaces-tree
db.version(7).stores({
  users: '++id, username',
  spaces: '++id, name, active',
  stations: '++id, name, active, spaceId',
  nodes: '++id, spaceId, name, active',
  products: '++id, name, price, category, active, nodeId',
  orders: '++id, uuid, stationId, nodeId, userId, username, createdAt, total, status, uploadStatus, syncedAt, remoteId',
  orderItems: '++id, orderId, productId, quantity, price, uploadStatus',
  productsByStations: '++id, spaceId, stationId, productId, name, active'
});

// Version 8: Add ordersReversed table to track locally reversed orders with reason
db.version(8).stores({
  users: '++id, username',
  spaces: '++id, name, active',
  stations: '++id, name, active, spaceId',
  nodes: '++id, spaceId, name, active',
  products: '++id, name, price, category, active, nodeId',
  orders: '++id, uuid, stationId, nodeId, userId, username, createdAt, total, status, uploadStatus, syncedAt, remoteId',
  orderItems: '++id, orderId, productId, quantity, price, uploadStatus',
  productsByStations: '++id, spaceId, stationId, productId, name, active',
  ordersReversed: '++id, originalOrderId, uuid, stationId, nodeId, userId, username, createdAt, total, reason, reversedAt'
});

// Auto-handle version changes and ensure DB stays open in dev/HMR
db.on('versionchange', () => {
  try {
    db.close();
  } catch {}
});

export const ensureDbOpen = async () => {
  if (!db.isOpen()) {
    await db.open();
  }
};

// Open database immediately on module import to avoid DatabaseClosedError after HMR
(async () => {
  try {
    if (!db.isOpen()) {
      await db.open();
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to open Dexie DB:', e);
  }
})();

// Initialize default data
export const initializeDefaultData = async () => {
  const userCount = await db.users.count();
  
  if (userCount === 0) {
    // Create default user
    await db.users.add({
      username: 'admin',
      password: 'admin123', // In production, use proper hashing
      name: 'Administrator'
    });
    
    // Create default stations
    await db.stations.bulkAdd([
      { name: 'Estación 1', active: true },
      { name: 'Estación 2', active: true },
      { name: 'Estación 3', active: true }
    ]);
    
    // Create default products
    await db.products.bulkAdd([
      { name: 'Café Americano', price: 2.50, category: 'Bebidas', active: true },
      { name: 'Café Latte', price: 3.50, category: 'Bebidas', active: true },
      { name: 'Cappuccino', price: 3.75, category: 'Bebidas', active: true },
      { name: 'Té Verde', price: 2.00, category: 'Bebidas', active: true },
      { name: 'Jugo Natural', price: 3.00, category: 'Bebidas', active: true },
      { name: 'Croissant', price: 2.50, category: 'Panadería', active: true },
      { name: 'Pan de Chocolate', price: 2.75, category: 'Panadería', active: true },
      { name: 'Muffin', price: 2.25, category: 'Panadería', active: true },
      { name: 'Sandwich', price: 5.50, category: 'Alimentos', active: true },
      { name: 'Ensalada', price: 6.00, category: 'Alimentos', active: true },
      { name: 'Galletas', price: 1.50, category: 'Postres', active: true },
      { name: 'Brownie', price: 3.00, category: 'Postres', active: true }
    ]);
  }
};

export default db;
